import client from "./client";

// Tiny in-memory cache for the read-mostly exam endpoints. Without it,
// every navigation back to the dashboard re-fetched the full exam list,
// and revisiting an exam refetched its parts and questions even though
// nothing had changed. The TTL is short enough that any teacher edits
// flushed via the admin UI become visible after one session-level
// refresh, and any mutation in this file calls invalidateExamCaches()
// to drop stale entries on the spot.
const TTL_MS = 30_000;
const examCache = new Map(); // key -> { value, expiresAt }
const examInflight = new Map(); // key -> Promise<value>

const cacheGet = (key) => {
  const hit = examCache.get(key);
  if (!hit) return undefined;
  if (hit.expiresAt < Date.now()) {
    examCache.delete(key);
    return undefined;
  }
  return hit.value;
};

const cacheSet = (key, value) => {
  examCache.set(key, { value, expiresAt: Date.now() + TTL_MS });
};

const invalidateExamCaches = () => {
  examCache.clear();
  examInflight.clear();
};

export const getExams = async (params = {}) => {
  const key = `list:${JSON.stringify(params)}`;
  const cached = cacheGet(key);
  if (cached !== undefined) return cached;
  const inflight = examInflight.get(key);
  if (inflight) return inflight;
  const promise = client.get("/exams/", {params}).then(({data}) => {
    cacheSet(key, data);
    examInflight.delete(key);
    return data;
  }).catch((err) => {
    examInflight.delete(key);
    throw err;
  });
  examInflight.set(key, promise);
  return promise;
};

export const getFetDashboard = async ({page = 1, pageSize = 5, skill = 'all', slim = false} = {}) => {
  // Pass pagination + skill filter params. The backend slices the
  // exam queryset BEFORE computing summaries, and filters by skill
  // before slicing — so each page is consistent within a skill tab.
  // In slim mode the backend skips the per-user stats (streak, score
  // samples, weekly activity, skills_aggregate) and returns just
  // `all_exams` + `all_exams_pagination` — used for pagination
  // clicks so they feel instant. The frontend keeps the stats it
  // received from the initial (non-slim) load.
  const params = {exams_page: page, exams_page_size: pageSize};
  if (skill && skill !== 'all') params.skill = skill;
  if (slim) params.slim = 1;
  const {data} = await client.get("/fet/dashboard/", {params});
  return data;
};

export const getExam = async (examId) => {
  const key = `exam:${examId}`;
  const cached = cacheGet(key);
  if (cached !== undefined) return cached;
  const inflight = examInflight.get(key);
  if (inflight) return inflight;
  const promise = client.get(`/exams/${examId}/`).then(({data}) => {
    cacheSet(key, data);
    examInflight.delete(key);
    return data;
  }).catch((err) => {
    examInflight.delete(key);
    throw err;
  });
  examInflight.set(key, promise);
  return promise;
};

export const getExamsAdmin = async (params = {}) => {
  const {data} = await client.get("/exams/", {params: {...params, include_inactive: 1}});
  return data;
};

export const getExamAdmin = async (examId) => {
  const {data} = await client.get(`/exams/${examId}/`, { params: { include_inactive: 1 } });
  return data;
};

export const createExam = async (payload) => {
  const {data} = await client.post("/exams/", payload);
  invalidateExamCaches();
  return data;
};

export const updateExam = async (examId, payload) => {
  const {data} = await client.put(`/exams/${examId}/`, payload);
  invalidateExamCaches();
  return data;
};

export const deleteExam = async (examId) => {
  await client.delete(`/exams/${examId}/`);
  invalidateExamCaches();
};

export const addWritingQuestion = async (examId, payload) => {
  const {data} = await client.post(`/exams/${examId}/questions/`, payload);
  invalidateExamCaches();
  return data;
};

export const updateWritingQuestion = async (examId, questionId, payload) => {
  const {data} = await client.put(
    `/exams/${examId}/questions/${questionId}/`,
    payload,
  );
  invalidateExamCaches();
  return data;
};

export const deleteWritingQuestion = async (examId, questionId) => {
  await client.delete(`/exams/${examId}/questions/${questionId}/`);
  invalidateExamCaches();
};

export const addSpeakingPart = async (examId, payload) => {
  const {data} = await client.post(`/exams/${examId}/speaking/`, payload);
  invalidateExamCaches();
  return data;
};

export const updateSpeakingPart = async (examId, partId, payload) => {
  const {data} = await client.put(`/exams/${examId}/speaking/${partId}/`, payload);
  invalidateExamCaches();
  return data;
};

export const deleteSpeakingPart = async (examId, partId) => {
  await client.delete(`/exams/${examId}/speaking/${partId}/`);
  invalidateExamCaches();
};

export const updateReadingPart = async (examId, partNumber, content) => {
  const {data} = await client.put(`/exams/${examId}/reading/${partNumber}/`, {
    content,
  });
  invalidateExamCaches();
  return data;
};

export const importReadingContent = async (examId, payload) => {
  const {data} = await client.post(`/exams/${examId}/reading-import/`, payload);
  invalidateExamCaches();
  return data;
};

export const importGeneralWritingExam = async ({exam_id, payload}) => {
  const body = exam_id ? {exam_id, payload} : {payload};
  const {data} = await client.post("/exams/general-writing-import/", body);
  invalidateExamCaches();
  return data;
};

export const importFetExam = async (payload) => {
  const {data} = await client.post("/exams/fet-import/", payload);
  invalidateExamCaches();
  return data;
};

export const createAttempt = async (
  examId,
  mode = "practice",
  section = "",
) => {
  const {data} = await client.post(
    "/attempts/",
    {
      exam_id: examId,
      mode,
      section,
    },
    {
      headers: {
        "X-ITC-App": "frontendFET",
      },
    },
  );
  return data;
};

export const getAttempt = async (attemptId) => {
  const {data} = await client.get(`/attempts/${attemptId}/`);
  return data;
};

export const getMyFetAttempts = async () => {
  const {data} = await client.get("/users/me/fet-attempts/");
  return data;
};

export const getMyAttempts = async () => {
  const {data} = await client.get("/users/me/attempts/");
  return data;
};

export const getCalendarEvents = async (limit = 6) => {
  const { data } = await client.get("/calendar/events/", { params: { limit } });
  return data;
};

export const setCalendarEventAway = async (eventId, away) => {
  const { data } = await client.post(`/calendar/events/${eventId}/opt-in/`, { away });
  return data;
};

export const listCalendarEventsAdmin = async () => {
  const { data } = await client.get("/calendar/admin/events/");
  return data;
};

export const createCalendarEventAdmin = async (payload) => {
  const { data } = await client.post("/calendar/admin/events/", payload);
  return data;
};

export const updateCalendarEventAdmin = async (eventId, payload) => {
  const { data } = await client.put(`/calendar/admin/events/${eventId}/`, payload);
  return data;
};

export const deleteCalendarEventAdmin = async (eventId) => {
  await client.delete(`/calendar/admin/events/${eventId}/`);
};

export const submitWriting = async (attemptId, responses) => {
  const {data} = await client.post(`/attempts/${attemptId}/writing/`, {
    responses,
  });
  return data;
};

export const submitReading = async (attemptId, answers) => {
  const {data} = await client.post(`/attempts/${attemptId}/reading/`, {
    answers,
  });
  return data;
};

export const pollAttemptUntilMarked = (
  attemptId,
  onUpdate,
  onError,
  intervalMs = 3000,
) => {
  let failureCount = 0;
  const startedAt = Date.now();
  const maxPollMs = 60000;

  const timer = setInterval(async () => {
    try {
      const attempt = await getAttempt(attemptId);
      failureCount = 0;
      onUpdate(attempt);
      const allDone = [...attempt.writing_responses].every(
        (response) =>
          response.mark_status === "done" || response.mark_status === "failed",
      );
      if (allDone) clearInterval(timer);
      if (!allDone && Date.now() - startedAt >= maxPollMs) {
        clearInterval(timer);
        onError?.(new Error("Polling timed out."));
      }
    } catch (error) {
      failureCount += 1;
      if (failureCount >= 3) {
        clearInterval(timer);
        onError?.(error);
      }
    }
  }, intervalMs);

  return () => clearInterval(timer);
};
